import { useNavigate } from 'react-router';
import { SecondaryButton } from './SecondaryButton';

export interface BookingItemProps {
  id: string;
  status: string;
  title: string;
  provider: string;
  price: string;
  frequency: string;
  duration: string;
  extra?: string;
  dateTime: string;
  badge: 'Active' | 'Canceled' | 'Ended';
  /** Service / category hero image. When null / empty we render a
   *  brand-neutral placeholder instead of a generic cleaning photo —
   *  the previous fallback misled customers on non-cleaning services. */
  image?: string | null;
  /** Where to navigate on row click. Defaults to /bookings/:id but
   *  subscription rows override to /billing-subscription so we don't
   *  send the customer to a booking-detail 404. */
  href?: string;
  /** Human-formatted "Booked on …" so the customer can see when they
   *  placed the order. Shown as a small caption above the date. */
  bookedOn?: string;
}

export function BookingItem({
  id,
  status,
  title,
  provider,
  price,
  frequency,
  duration,
  extra,
  dateTime,
  badge,
  image,
  href,
  bookedOn,
}: BookingItemProps) {
  const navigate = useNavigate();
  
  const badgeStyles = {
    Active: 'bg-[#f2eee2] text-[#8e2a2f]',
    Canceled: 'bg-[#fcecec] text-[#e53935]',
    Ended: 'bg-[#f8f8f8] text-[#908e8e]',
  };

  const statusTextStyles = {
    Active: 'text-[#908e8e]',
    Canceled: 'text-[#e53935]',
    Ended: 'text-[#908e8e]',
  };

  return (
    <div 
      className="bg-white border border-[#eee] rounded-[10px] overflow-hidden mb-4 flex flex-col md:flex-row hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(href ?? `/bookings/${id}`)}
    >
      {/* Image — real service / category hero when we have one, brand
          placeholder when we don't. Treat blank strings the same as
          null so legacy callers passing '' still get the placeholder. */}
      <div className="w-full md:w-[251px] shrink-0 overflow-hidden h-[178px] bg-[#f2eee2]">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            aria-label="No image"
            role="img"
          >
            <span className="material-icons text-[#8e2a2f]/40 text-[64px]">
              image
            </span>
          </div>
        )}
      </div>

      {/* Content Wrapper with Padding */}
      <div className="flex-1 flex flex-col md:flex-row gap-6 p-4 md:p-6">
        {/* Content */}
        <div className="flex-1 flex flex-col justify-between">
          <div>
            <p className={`font-['DM_Sans',sans-serif] text-xs leading-[18px] tracking-[-0.1px] mb-2 ${statusTextStyles[badge]}`} style={{ fontVariationSettings: "'opsz' 14" }}>
              {status}
            </p>
            <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[20px] leading-[28px] mb-1">
              {title}
            </h3>
            <p className="font-['Playfair_Display',sans-serif] text-[#908e8e] text-[18px] leading-[21px] mb-4">
              with {provider}
            </p>
            
            <div className="flex flex-wrap items-center gap-2 font-['DM_Sans',sans-serif] text-sm leading-[21px]">
              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-base">{price}</span>
              <span className="text-[#908e8e]">•</span>
              <span className="text-[#908e8e]">{frequency}</span>
              <span className="text-[#908e8e]">•</span>
              <span className="text-[#908e8e]">{duration}</span>
              {extra && (
                <>
                  <span className="text-[#908e8e]">•</span>
                  <span className="text-[#908e8e]">{extra}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Date & Badge */}
        <div className="flex flex-col justify-between items-end shrink-0 gap-2">
          <div className="text-right">
            {bookedOn ? (
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] uppercase tracking-wider font-bold mb-0.5">
                Booked on {bookedOn}
              </p>
            ) : null}
            <div className="flex items-center gap-2 justify-end">
              <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-sm leading-[21px]">
                {dateTime}
              </p>
              <button className="material-icons text-[#908e8e] text-lg cursor-pointer">more_horiz</button>
            </div>
          </div>

          <div className={`px-4 py-1.5 rounded-lg font-['DM_Sans',sans-serif] font-bold text-xs ${badgeStyles[badge]}`}>
            {badge}
          </div>
        </div>
      </div>
    </div>
  );
}
